/**
 * MandatAufloesenDialog - the dialog «Mandat auflösen» (Stefan P6 V1.0 steps 3, 5, 6, 7; UC4-UC7, UC11).
 *
 * Opened from the status menu («Gekündigt», detail header and Kundenliste) and from the area «Mandate &
 * Eintragungen». It collects the mandate kind, the Eingangsdatum (default today, not in the future: the
 * reference of the Fristregel and of the Rückdatierung, 7.6/7.7), the Art (sofort / per Stichtag), the
 * Enddatum (empty = the tenant's Fristregel from the Eingangsdatum, shown as «abgeleitet: <date>»), the Grund, the mandatory Begründung and an optional
 * Beleg, shows the Folgenanalyse, and sends ONE request: open with the Freigabe (decision 27.10.2026), so
 * nothing half-done remains.
 *
 * A live termination of the customer is resumed instead: an eröffnete one (the Rücklauf, a phone call
 * taken earlier) is approved on its own id or rejected, a wirksame one offers the Widerruf.
 *
 * From a Kündigung the intake read from a mail (the task «Mögliche Kündigung prüfen»), the dialog is opened
 * with the `proposal`: the mandate kind, the Art and the Enddatum are prefilled from it (the staff confirms or
 * corrects them), the Eingangsdatum is the mail's receipt day (fixed, decision 19), the Grund is «Kündigung per
 * E-Mail». «Eröffnen» applies the proposal (POST /tasks/:taskId/termination-proposal/apply: the termination is
 * opened, never approved, and the Berechtigungsprüfung runs); with the Freigabe key the dialog then resumes the
 * opened termination for the Freigabe, otherwise it closes.
 *
 * The keys: opening needs mandate.eingangskontrolle, the Freigabe mandate.dispatch (the
 * P4/P5 Freigabe key; without it the dialog only opens, «Eröffnen», and the termination waits for the
 * Freigabe), the Widerruf and the Rückdatierung customer_status.override.
 *
 * The api decides every rule: the Enddatum plausibility, the backdate key (customer_status.override),
 * the guards. The date arithmetic here is only the display of the api's own rule (end_date_rule of the
 * preview); every error is shown with the api's message and its hint.
 */

import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  MANUAL_TERMINATION_REASON_CODES,
  TERMINATION_HINT_ABBRUCH,
  TERMINATION_REASON_LABEL,
  TERMINATION_REMARK_MAX,
  TERMINATION_STATUS_LABEL,
  RECEIVED_ON_MAX_AGE_DAYS,
  addDaysIso,
  formatIsoDayDe,
  fristregelEndDate,
  zurichDateOf,
  gekuendigtPerLabel,
  isIsoDay,
  terminationMandateLabel,
} from '@brokly/shared';
import type { TerminationArt, TerminationDto, TerminationImpactDto, TerminationProposalDto, TerminationReasonCode } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { Dialog } from '../../administration/components';
import { useEffectivePermissions } from '../../administration/useEffectivePermissions';
import { ApiError, apiClient } from '../../../lib/api-client';
import type { ApproveTerminationBody, OpenTerminationBody } from '../../../lib/api-client';
import { qk } from '../../../lib/query-keys';
import { refreshMandateQueries } from '../../entity-detail/mandate-state-queries';
import { useClientDocumentRows } from '../../documents/hooks/useClientDocumentRows';

type SubjectType = 'person' | 'firma';
type PastChoice = 'heute' | 'rueckdatierung' | null;

/** The hint codes the api sends next to an error, as one German sentence each. */
const HINT_TEXT: Record<string, string> = {
  [TERMINATION_HINT_ABBRUCH]: 'Den laufenden Mandatsvorgang abbrechen statt das Mandat aufzulösen.',
};

export function terminationErrorText(err: unknown): { message: string; hint: string | null } {
  if (err instanceof ApiError) {
    return { message: err.message, hint: err.hint ? (HINT_TEXT[err.hint] ?? null) : null };
  }
  return { message: 'Die Mandatsauflösung konnte nicht gespeichert werden.', hint: null };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The checklist of the Folgenanalyse (7.1 step 6): counts only, the api's read model. */
export function FolgenanalyseList({ impact, endDate }: { impact: TerminationImpactDto; endDate: string | null }) {
  const bis = endDate ? ` bis ${formatIsoDayDe(endDate)}` : '';
  const items = [
    plural(impact.registrations.p5_broker.live, 'Eintragung Brokermandat', 'Eintragungen Brokermandat'),
    plural(impact.registrations.p4_analyse.live, 'Eintragung Analysemandat', 'Eintragungen Analysemandat'),
    `${plural(impact.policies.active, 'aktive Police', 'aktive Policen')}, davon ${impact.policies.courtage_bearing} courtageberechtigt${bis}`,
    plural(impact.placements_open, 'offene Ausschreibung', 'offene Ausschreibungen'),
    plural(impact.claims_open, 'offener Schadenfall', 'offene Schadenfälle'),
    plural(impact.tasks_open, 'offene Aufgabe', 'offene Aufgaben'),
    plural(impact.vorgaenge_open, 'offener Mandatsvorgang', 'offene Mandatsvorgänge'),
    plural(impact.mahnlaeufe_open, 'laufender Mahnlauf bei Versicherern', 'laufende Mahnläufe bei Versicherern'),
    ...(impact.portal === 'active' ? ['Kundenportal aktiv'] : []),
    ...(impact.retention_until ? [`Aufbewahrung bis ${formatIsoDayDe(impact.retention_until)}`] : []),
  ];
  return (
    <ul className="flex flex-col gap-0.5 text-sm" data-testid="folgenanalyse">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

export function MandatAufloesenDialog({
  subjectType,
  subjectId,
  onClose,
  onDone,
  proposal,
}: {
  subjectType: SubjectType;
  subjectId: string;
  onClose: () => void;
  /** After a successful Freigabe, Zurückweisung or Widerruf (the queries are already refreshed). */
  onDone?: (result: TerminationDto) => void;
  /** The Kündigung the intake read from a mail: the dialog applies it instead of a manual open. */
  proposal?: TerminationProposalDto;
}) {
  const qc = useQueryClient();
  const { can } = useEffectivePermissions();
  const canOpen = can('mandate.eingangskontrolle');
  const canApprove = can('mandate.dispatch');
  const canRevoke = can('customer_status.override');
  const canBackdate = canRevoke;

  const liveQuery = useQuery({
    queryKey: qk.termination(subjectType, subjectId),
    queryFn: () => apiClient.mandateTermination.live(subjectType, subjectId),
  });
  const badges = useQuery({
    queryKey: qk.mandateBadges(subjectType, subjectId),
    queryFn: () => apiClient.mandateWizard.badges(subjectType, subjectId),
  });
  const { rows: documents } = useClientDocumentRows(subjectId);

  const live = liveQuery.data ?? null;
  const resuming = live !== null && (live.status === 'eroeffnet' || live.status === 'in_pruefung');
  const wirksam = live !== null && live.status === 'wirksam';
  // A proposal is applied only while the customer has no live termination (else that one is resumed).
  const applying = proposal !== undefined && live === null && liveQuery.isSuccess;
  // Without the Freigabe key a new termination is only opened (no approve in the body).
  const openOnly = !resuming && canOpen && !canApprove;
  // The step only opens (no Freigabe in this request): the date checks of the Freigabe come with it.
  const opensOnly = openOnly || applying;
  const canEdit = resuming ? canOpen || canApprove : canOpen;

  // ── the form ───────────────────────────────────────────────────────────────────────────────
  const brokerSigned = badges.data?.broker.state === 'unterzeichnet';
  const analyseSigned = badges.data?.analyse.state === 'unterzeichnet';
  const signedDefault: 'broker' | 'analyse' | '' = brokerSigned ? 'broker' : analyseSigned ? 'analyse' : '';
  const proposedKind = proposal?.mandateKind;
  const defaultKind: 'broker' | 'analyse' | '' =
    applying && proposedKind === 'analyse' && analyseSigned && !brokerSigned
      ? 'analyse'
      : applying && proposedKind === 'broker' && brokerSigned
        ? 'broker'
        : signedDefault;
  const [kindPick, setKindPick] = useState<'broker' | 'analyse' | '' | null>(null);
  const kind = kindPick ?? defaultKind;
  const [artPick, setArtPick] = useState<TerminationArt | null>(null);
  const [datePick, setDatePick] = useState<string | null>(null);
  const [reason, setReason] = useState<TerminationReasonCode>('kuendigung_telefonisch');
  const [remark, setRemark] = useState('');
  const [beleg, setBeleg] = useState('');
  const [pastChoice, setPastChoice] = useState<PastChoice>(null);
  const [receivedPick, setReceivedPick] = useState<string | null>(null);

  // A resumed termination brings its own Art and date; a pick by the broker wins.
  const art: TerminationArt =
    artPick ?? (resuming ? (live.art ?? 'stichtag') : applying ? (proposal.art === 'sofort' ? 'sofort' : 'stichtag') : 'sofort');
  const dateInput = datePick ?? (resuming ? (live.requestedEndDate ?? '') : applying ? (proposal.endDate ?? '') : '');
  const named = art === 'stichtag' && isIsoDay(dateInput) ? dateInput : null;

  // The tenant's rule (today, the Fristregel and its days) comes with the preview.
  const [ruleEndDate, setRuleEndDate] = useState<string | null>(null);
  const preview = useQuery({
    queryKey: qk.terminationFolgenanalyse(subjectType, subjectId, ruleEndDate),
    queryFn: () => apiClient.mandateTermination.preview(subjectType, subjectId, ruleEndDate),
    placeholderData: keepPreviousData,
  });
  const rule = preview.data?.end_date_rule ?? null;

  // The Eingangsdatum is the reference (7.6/7.7, UC7): sofort ends on it, the Fristregel counts from it,
  // an Enddatum before it is a Rückdatierung. A resumed termination brings its own.
  // With a Beleg the Eingangsdatum is prefilled from the day it reached Brokly (Zurich), editable.
  const belegRow = beleg !== '' ? documents.find((d) => d.id === beleg) : undefined;
  const belegDay = belegRow?.receivedAt ? zurichDateOf(belegRow.receivedAt) : null;
  const receivedInput = receivedPick ?? belegDay ?? rule?.today ?? '';
  // Not in the future; at most RECEIVED_ON_MAX_AGE_DAYS back without the backdate key.
  const receivedMin = rule !== null && !canBackdate ? addDaysIso(rule.today, -RECEIVED_ON_MAX_AGE_DAYS) : undefined;
  const receivedTooOld = receivedMin !== undefined && isIsoDay(receivedInput) && receivedInput < receivedMin;
  const receivedOn: string | null = resuming
    ? (live.receivedOn ?? rule?.today ?? null)
    : applying
      ? (proposal.receivedOn ?? rule?.today ?? null)
      : isIsoDay(receivedInput) && rule !== null && receivedInput <= rule.today && !receivedTooOld
      ? receivedInput
      : null;
  const derivedEndDate = rule !== null && receivedOn !== null ? fristregelEndDate(rule.fristregel, rule.fristregel_tage, receivedOn) : null;
  const sofortDate = receivedOn;
  const baseEndDate = art === 'sofort' ? sofortDate : (named ?? derivedEndDate);
  const isPast = baseEndDate !== null && receivedOn !== null && baseEndDate < receivedOn;
  const choice: PastChoice = isPast ? pastChoice : null;
  const endDate = choice === 'heute' ? receivedOn : baseEndDate;
  if (endDate !== ruleEndDate) setRuleEndDate(endDate);

  const source: ApproveTerminationBody['endDateSource'] =
    art === 'stichtag' && named === null
      ? 'abgeleitet'
      : resuming && datePick === null && art === 'stichtag' && live.trigger !== 'manuell'
        ? 'dokument'
        : 'erfasst';

  // P6-S3 (concept step 7): the Dankesbrief as it will read, from the api's own text path. Read-only.
  const showLetter = !openOnly && !wirksam && endDate !== null && receivedOn !== null;
  const letterPreview = useQuery({
    queryKey: qk.terminationLetterPreview(subjectType, subjectId, endDate ?? '', receivedOn ?? '', kind),
    queryFn: () =>
      apiClient.mandateTermination.letterPreview({
        subjectType,
        subjectId,
        endDate: endDate!,
        receivedOn,
        mandateKind: kind !== '' ? kind : null,
        trigger: resuming ? live.trigger : 'manuell',
      }),
    enabled: showLetter,
    placeholderData: keepPreviousData,
  });

  const remarkOk = remark.trim() !== '';
  const ready = applying
    ? receivedOn !== null && (art === 'sofort' || dateInput === '' || named !== null)
    : openOnly
    ? remarkOk && receivedOn !== null
    : receivedOn !== null && endDate !== null && (!isPast || choice !== null) && (resuming || remarkOk);

  // ── the writes ─────────────────────────────────────────────────────────────────────────────
  const [error, setError] = useState<unknown>(null);
  function done(result: TerminationDto) {
    void qc.invalidateQueries({ queryKey: qk.termination(subjectType, subjectId) });
    // (qk.termination is the prefix of qk.terminationFolgenanalyse: the Folgenanalyse is refreshed too.)
    refreshMandateQueries(qc, subjectId, subjectType);
    // P6-S3 review L7: a new Dankesbrief shows on the board without a reload.
    void qc.invalidateQueries({ queryKey: ['mandate', 'termination-letters'] });
    // The letter row reads the termination by id (not under the per-customer key): a Widerruf changes it.
    void qc.invalidateQueries({ queryKey: qk.terminationById(result.id) });
    onDone?.(result);
  }
  function failed(err: unknown) {
    setError(err);
    // Someone opened one meanwhile: load it, the dialog resumes it. Only then: a failed open + approve
    // rolled back as a whole and names no termination, there is nothing to resume.
    if (err instanceof ApiError && err.code === 'termination_open') void liveQuery.refetch();
  }

  const submit = useMutation<TerminationDto, unknown, void>({
    mutationFn: () => {
      if (openOnly) {
        return apiClient.mandateTermination.open({
          subjectType,
          subjectId,
          ...(kind !== '' ? { mandateKind: kind } : {}),
          art,
          ...(art === 'stichtag' && named !== null ? { requestedEndDate: named } : {}),
          reasonCode: reason,
          remark: remark.trim(),
          ...(beleg !== '' ? { evidenceDocumentId: beleg } : {}),
          ...(receivedOn !== null ? { receivedOn } : {}),
        });
      }
      const approve: ApproveTerminationBody = {
        endDate: endDate!,
        endDateSource: choice === 'heute' ? 'erfasst' : source,
        ...(choice === 'rueckdatierung' ? { backdate: true } : {}),
      };
      if (resuming) return apiClient.mandateTermination.approve(live.id, approve);
      const sofort = art === 'sofort' || choice === 'heute';
      const body: OpenTerminationBody = {
        subjectType,
        subjectId,
        ...(kind !== '' ? { mandateKind: kind } : {}),
        art: sofort ? 'sofort' : 'stichtag',
        ...(!sofort && named !== null ? { requestedEndDate: named } : {}),
        reasonCode: reason,
        remark: remark.trim(),
        ...(beleg !== '' ? { evidenceDocumentId: beleg } : {}),
        ...(receivedOn !== null ? { receivedOn } : {}),
        approve,
      };
      return apiClient.mandateTermination.open(body);
    },
    onMutate: () => setError(null),
    onSuccess: done,
    onError: failed,
  });
  const reject = useMutation<TerminationDto, unknown, void>({
    mutationFn: () => apiClient.mandateTermination.reject(live!.id, remark.trim()),
    onMutate: () => setError(null),
    onSuccess: done,
    onError: failed,
  });
  // P6-S3 round 2 C1: a Dankesbrief waiting for print may have been posted already: ask before the Widerruf.
  const letterWaitsForPrint = wirksam && live.letter?.status === 'ausstehend' && live.letter.channel === 'post';
  const [letterPosted, setLetterPosted] = useState<'ja' | 'nein' | null>(null);
  const revoke = useMutation<TerminationDto, unknown, void>({
    mutationFn: () =>
      apiClient.mandateTermination.revoke(live!.id, remark.trim(), letterWaitsForPrint ? { letterAlreadySent: letterPosted === 'ja' } : {}),
    onMutate: () => setError(null),
    onSuccess: done,
    onError: failed,
  });
  const apply = useMutation<{ terminationId: string; status: string; taskId: string }, unknown, void>({
    mutationFn: () =>
      apiClient.mandateTermination.applyProposal(proposal!.taskId!, {
        ...(kind !== '' ? { mandateKind: kind } : {}),
        art,
        requestedEndDate: art === 'stichtag' ? named : null,
      }),
    onMutate: () => setError(null),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.termination(subjectType, subjectId) });
      void qc.invalidateQueries({ queryKey: ['tasks'] });
      if (proposal?.taskId) void qc.invalidateQueries({ queryKey: qk.terminationProposal(proposal.taskId) });
      refreshMandateQueries(qc, subjectId, subjectType);
      // Opened without a Befund and the caller holds the Freigabe key: the dialog resumes it (the live read).
      if (!(r.status === 'eroeffnet' && canApprove)) onClose();
    },
    onError: failed,
  });
  const busy = submit.isPending || reject.isPending || revoke.isPending || apply.isPending;
  const errText = error !== null ? terminationErrorText(error) : null;

  // ── render ─────────────────────────────────────────────────────────────────────────────────
  const loading = liveQuery.isLoading || badges.isLoading;
  const remarkField = (label: string) => (
    <label className="flex flex-col gap-1 text-sm">
      {label}
      <textarea
        aria-label="Begründung"
        className="min-h-16 rounded border px-2 py-1 text-sm"
        maxLength={TERMINATION_REMARK_MAX}
        value={remark}
        onChange={(e) => setRemark(e.target.value)}
      />
    </label>
  );

  const showApprove = !applying && canApprove && (resuming || canOpen);
  // A Befund is open (in_pruefung): the Freigabe waits for «Berechtigung bestätigt» (api 409 termination_check_open).
  const checkOpen = resuming && live.status === 'in_pruefung';
  const showReject = resuming && canOpen;
  const footer = (wirksam ? !canRevoke : !showApprove && !opensOnly && !showReject) ? (
    <Button variant="outline" onClick={onClose}>
      Schliessen
    </Button>
  ) : wirksam ? (
    <>
      <Button variant="outline" onClick={onClose}>
        Abbrechen
      </Button>
      <Button disabled={!remarkOk || busy || (letterWaitsForPrint && letterPosted === null)} onClick={() => revoke.mutate()}>
        Kündigung widerrufen
      </Button>
    </>
  ) : (
    <>
      <Button variant="outline" onClick={onClose}>
        Abbrechen
      </Button>
      {showReject && (
        <Button variant="outline" disabled={!remarkOk || busy} onClick={() => reject.mutate()}>
          Zurückweisen
        </Button>
      )}
      {applying && canOpen ? (
        <Button disabled={!ready || busy || loading} onClick={() => apply.mutate()}>
          Eröffnen
        </Button>
      ) : (
        (showApprove || openOnly) && (
          <Button disabled={!ready || busy || loading || checkOpen} onClick={() => submit.mutate()}>
            {openOnly ? 'Eröffnen' : 'Mandat auflösen'}
          </Button>
        )
      )}
    </>
  );

  return (
    <Dialog open onClose={onClose} title="Mandat auflösen" wide footer={footer}>
      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" role="status">
          <Loader2 className="size-4 animate-spin" /> Lädt …
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {!wirksam && !canEdit && <p className="text-sm text-muted-foreground">Keine Berechtigung, ein Mandat aufzulösen.</p>}
          {checkOpen && (
            <p className="text-sm text-status-warning" data-testid="termination-check-open">
              Die Berechtigungsprüfung hat einen Befund. Zuerst in der Aufgabe «Kündigung prüfen» die Berechtigung bestätigen, dann
              freigeben.
            </p>
          )}
          {!wirksam && canOpen && !canApprove && (
            <p className="text-sm text-muted-foreground" data-testid="termination-no-approve">
              Die Freigabe braucht die Berechtigung zur Mandatsfreigabe.
              {resuming ? ' Die Mandatsauflösung bleibt offen, bis sie freigegeben wird.' : ' Die Mandatsauflösung wird nur eröffnet und bleibt offen, bis sie freigegeben wird.'}
            </p>
          )}

          {wirksam && live && (
            <div className="flex flex-col gap-3" data-testid="termination-wirksam">
              <p className="text-sm">
                {live.processNumber} · {terminationMandateLabel(live.mandateKind)}{' '}
                <span className="font-medium">{gekuendigtPerLabel(live.endDate)}</span>
              </p>
              {canRevoke && letterWaitsForPrint && (
                <fieldset className="flex flex-col gap-1 text-sm" data-testid="widerruf-brief-frage">
                  <legend>Bestätigung bereits per Post versendet?</legend>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="brief-versendet" checked={letterPosted === 'ja'} onChange={() => setLetterPosted('ja')} />
                    Ja, sie ist per Post versendet
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="brief-versendet" checked={letterPosted === 'nein'} onChange={() => setLetterPosted('nein')} />
                    Nein, sie wird storniert
                  </label>
                </fieldset>
              )}
              {canRevoke ? (
                remarkField('Begründung des Widerrufs')
              ) : (
                <p className="text-sm text-muted-foreground">Den Widerruf gibt ein Team Lead frei.</p>
              )}
            </div>
          )}

          {!wirksam && (
            <>
              {resuming && live ? (
                <p className="text-sm" data-testid="termination-resume">
                  {live.processNumber} · {TERMINATION_STATUS_LABEL[live.status]} · {TERMINATION_REASON_LABEL[live.reasonCode]}
                  {live.receivedOn ? ` · eingegangen ${formatIsoDayDe(live.receivedOn)}` : ''}
                </p>
              ) : (
                <label className="flex flex-col gap-1 text-sm">
                  Mandat
                  <select
                    aria-label="Mandat"
                    className="w-72 rounded border px-2 py-1 text-sm"
                    value={kind}
                    disabled={!canEdit}
                    onChange={(e) => setKindPick(e.target.value as 'broker' | 'analyse' | '')}
                  >
                    {!brokerSigned && !analyseSigned && <option value="">Kein unterzeichnetes Mandat</option>}
                    <option value="broker" disabled={!brokerSigned}>
                      Brokermandat
                    </option>
                    {/* A signed Brokermandat ends the whole relationship (7.8): the Analysemandat is not picked alone then. */}
                    <option value="analyse" disabled={!analyseSigned || brokerSigned}>
                      Analysemandat
                    </option>
                  </select>
                </label>
              )}

              {applying && (
                <p className="text-sm" data-testid="termination-from-mail">
                  {proposal.channel === 'upload' ? 'Kündigung hochgeladen' : 'Kündigung per E-Mail'}{receivedOn ? ` · eingegangen ${formatIsoDayDe(receivedOn)}` : ''}
                </p>
              )}

              {!resuming && !applying && (
                <label className="flex flex-col gap-1 text-sm">
                  Eingangsdatum
                  <input
                    type="date"
                    aria-label="Eingangsdatum"
                    className="w-48 rounded border px-2 py-1 text-sm"
                    value={receivedInput}
                    max={rule?.today}
                    min={receivedMin}
                    disabled={!canEdit}
                    onChange={(e) => setReceivedPick(e.target.value)}
                  />
                  {receivedTooOld ? (
                    <span className="text-xs text-destructive">
                      Das Eingangsdatum liegt mehr als {RECEIVED_ON_MAX_AGE_DAYS} Tage zurück; das gibt ein Team Lead frei.
                    </span>
                  ) : (
                    receivedInput !== '' && receivedOn === null && rule && (
                      <span className="text-xs text-destructive">Das Eingangsdatum darf nicht in der Zukunft liegen.</span>
                    )
                  )}
                </label>
              )}

              <fieldset className="flex flex-col gap-2 text-sm" disabled={!canEdit}>
                <legend className="mb-1">Art der Auflösung</legend>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-1">
                    <input type="radio" name="art" checked={art === 'sofort'} onChange={() => setArtPick('sofort')} />
                    sofort
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="radio" name="art" checked={art === 'stichtag'} onChange={() => setArtPick('stichtag')} />
                    per Stichtag
                  </label>
                </div>
                {art === 'stichtag' && (
                  <label className="flex flex-col gap-1">
                    Enddatum
                    <input
                      type="date"
                      aria-label="Enddatum"
                      className="w-48 rounded border px-2 py-1 text-sm"
                      value={dateInput}
                      onChange={(e) => setDatePick(e.target.value)}
                    />
                    {named === null && derivedEndDate && (
                      <span className="text-xs text-muted-foreground">abgeleitet: {formatIsoDayDe(derivedEndDate)}</span>
                    )}
                  </label>
                )}
                {art === 'sofort' && sofortDate && (
                  <span className="text-xs text-muted-foreground">Enddatum {formatIsoDayDe(sofortDate)}</span>
                )}
                {endDate !== null && rule !== null && endDate < rule.today && !opensOnly && (
                  <span className="text-xs text-destructive" data-testid="termination-end-past">
                    Das Enddatum liegt vor heute: der Kunde wird im Nachtlauf nach dem Tag der Freigabe inaktiv. Bis dahin ist ein
                    Widerruf möglich.
                  </span>
                )}
                {isPast && !opensOnly && (
                  <div className="flex flex-col gap-1 rounded border border-dashed p-2" data-testid="termination-past">
                    <span className="text-xs">Das Enddatum liegt vor dem Eingangsdatum.</span>
                    <label className="flex items-center gap-1">
                      <input type="radio" name="past" checked={choice === 'heute'} onChange={() => setPastChoice('heute')} />
                      sofort per Eingangsdatum
                    </label>
                    <label className="flex items-center gap-1" title={canBackdate ? undefined : 'Die Rückdatierung gibt ein Team Lead frei.'}>
                      <input
                        type="radio"
                        name="past"
                        checked={choice === 'rueckdatierung'}
                        disabled={!canBackdate}
                        onChange={() => setPastChoice('rueckdatierung')}
                      />
                      Rückdatierung (Freigabe Team Lead)
                    </label>
                  </div>
                )}
              </fieldset>

              {!resuming && !applying && (
                <>
                  <label className="flex flex-col gap-1 text-sm">
                    Grund
                    <select
                      aria-label="Grund"
                      className="w-72 rounded border px-2 py-1 text-sm"
                      value={reason}
                      disabled={!canEdit}
                      onChange={(e) => setReason(e.target.value as TerminationReasonCode)}
                    >
                      {MANUAL_TERMINATION_REASON_CODES.map((c) => (
                        <option key={c} value={c}>
                          {TERMINATION_REASON_LABEL[c]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="flex flex-col gap-1 text-sm">
                    Beleg
                    <select
                      aria-label="Beleg"
                      className="w-72 max-w-full rounded border px-2 py-1 text-sm"
                      value={beleg}
                      disabled={!canEdit}
                      onChange={(e) => {
                        setBeleg(e.target.value);
                        // a new Beleg brings its own receipt day (the broker can still change it)
                        setReceivedPick(null);
                      }}
                    >
                      <option value="">Kein Beleg</option>
                      {documents.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.title}
                        </option>
                      ))}
                    </select>
                  </label>
                </>
              )}
              {canEdit && !applying && remarkField(resuming ? 'Begründung (Pflicht beim Zurückweisen)' : 'Begründung (Pflicht)')}

              <div className="flex flex-col gap-1">
                <h3 className="text-xs font-semibold uppercase text-muted-foreground">Folgenanalyse</h3>
                {preview.data ? (
                  <FolgenanalyseList impact={preview.data} endDate={endDate} />
                ) : preview.isError ? (
                  <p className="text-xs text-muted-foreground">Die Folgenanalyse konnte nicht geladen werden.</p>
                ) : (
                  <p className="text-xs text-muted-foreground">Lädt …</p>
                )}
              </div>

              {showLetter && (
                <div className="flex flex-col gap-1" data-testid="dankesbrief-vorschau">
                  <h3 className="text-xs font-semibold uppercase text-muted-foreground">Bestätigung an den Kunden (Vorschau)</h3>
                  {letterPreview.data ? (
                    <div className="max-h-48 overflow-y-auto rounded border bg-muted/30 p-2 text-xs">
                      <p className="font-semibold">{letterPreview.data.subject}</p>
                      <p className="mt-1 whitespace-pre-line">{letterPreview.data.body}</p>
                    </div>
                  ) : letterPreview.isError ? (
                    <p className="text-xs text-muted-foreground">Die Vorschau konnte nicht geladen werden.</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Lädt …</p>
                  )}
                  <p className="text-xs text-muted-foreground">Nach der Freigabe geht sie über die Versandsteuerung an den Kunden.</p>
                </div>
              )}
            </>
          )}

          {errText && (
            <div className="flex items-start gap-2 text-sm text-destructive" role="alert">
              <AlertTriangle className="mt-0.5 size-4 shrink-0" />
              <div>
                <p>{errText.message}</p>
                {errText.hint && <p>{errText.hint}</p>}
              </div>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
